/**
 * PropertyMapView - Core Reusable Property Map View Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides an Airbnb-styled split view with map on right, property list on left.
 * Uses Tailwind CSS for styling - themes can override via className prop.
 */

'use client';

import type { Property } from '@/lib/api/clients/properties';
import { extractBestPriceSimple } from '@/lib/utils/price';
import { useCallback, useState } from 'react';

export interface PropertyMapViewProps {
  /**
   * Properties to display
   */
  properties: Property[];
  /**
   * Organisation ID for generating links
   */
  organisationId: string;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Custom map component renderer
   * Themes can pass their own map implementation
   */
  renderMap?: (props: MapRendererProps) => React.ReactNode;
  /**
   * Custom card renderer for property list
   * Themes can pass their own card implementation
   */
  renderCard?: (property: Property, isSelected: boolean) => React.ReactNode;
  /**
   * Map position: 'left' or 'right'
   * @default 'right'
   */
  mapPosition?: 'left' | 'right';
  /**
   * Empty state message
   */
  emptyMessage?: string;
  /**
   * Height of the map view container
   * @default 'calc(100vh - 320px)'
   */
  height?: string;
}

export interface MapRendererProps {
  properties: Property[];
  selectedPropertyId: string | null;
  hoveredPropertyId?: string | null;
  onMarkerClick: (property: Property) => void;
  className?: string;
}

/**
 * Core Property Map View Component
 * 
 * Airbnb-styled split layout with interactive map and scrollable property list.
 * Provides selection syncing between map markers and list items.
 */
export function PropertyMapView({
  properties,
  organisationId,
  locale = 'en',
  className = '',
  renderMap,
  renderCard,
  mapPosition = 'right',
  emptyMessage = 'No properties available',
  height = 'calc(100vh - 320px)',
}: PropertyMapViewProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    properties.length > 0 ? properties[0]!.id : null
  );
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);

  // Handle property card click
  const handlePropertyClick = useCallback((property: Property) => {
    setSelectedPropertyId(property.id);
  }, []);

  // Handle property card hover
  const handlePropertyHover = useCallback((propertyId: string | null) => {
    setHoveredPropertyId(propertyId);
  }, []);

  // Handle map marker click
  const handleMarkerClick = useCallback((property: Property) => {
    setSelectedPropertyId(property.id);
    
    // Scroll to property in list
    const element = document.getElementById(`map-property-${property.id}`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, []);

  // Empty state
  if (properties.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ minHeight: height }}>
        <div className="text-center p-8">
          <svg className="w-20 h-20 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <p className="text-lg text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Determine layout order
  const isMapRight = mapPosition === 'right';

  return (
    <div 
      className={`flex flex-col lg:flex-row gap-0 ${className}`}
      style={{ height }}
    >
      {/* Property List Section - Left side (or right if mapPosition is 'left') */}
      <div 
        className={`w-full lg:w-1/2 overflow-y-auto border-r border-gray-200 bg-gray-50 ${
          isMapRight ? 'order-1' : 'order-2'
        }`}
      >
        <div className="p-4 space-y-3">
          {properties.map((property) => {
            const isSelected = selectedPropertyId === property.id;
            const isHovered = hoveredPropertyId === property.id;
            
            return (
              <div
                key={property.id}
                id={`map-property-${property.id}`}
                onClick={() => handlePropertyClick(property)}
                onMouseEnter={() => handlePropertyHover(property.id)}
                onMouseLeave={() => handlePropertyHover(null)}
                className="cursor-pointer"
              >
                {renderCard ? (
                  renderCard(property, isSelected || isHovered)
                ) : (
                  <DefaultPropertyCard 
                    property={property} 
                    organisationId={organisationId}
                    locale={locale}
                    isSelected={isSelected || isHovered}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Map Section - Right side (or left if mapPosition is 'left') */}
      <div 
        className={`w-full lg:w-1/2 h-full sticky top-0 ${
          isMapRight ? 'order-2' : 'order-1'
        }`}
      >
          {renderMap ? (
            renderMap({
              properties,
              selectedPropertyId,
              hoveredPropertyId,
              onMarkerClick: handleMarkerClick,
              className: 'w-full h-full',
            })
          ) : (
            <DefaultMapRenderer
              properties={properties}
              selectedPropertyId={selectedPropertyId}
              hoveredPropertyId={hoveredPropertyId}
              onMarkerClick={handleMarkerClick}
            />
          )}
      </div>
    </div>
  );
}

/**
 * Default Property Card (Placeholder)
 * 
 * Shows a simple card until themes provide their own implementation.
 */
function DefaultPropertyCard({
  property,
  organisationId: _organisationId,
  locale: _locale,
  isSelected,
}: {
  property: Property;
  organisationId: string;
  locale: string;
  isSelected: boolean;
}) {
  const { amount: askingPrice, currency: priceCurrency } = extractBestPriceSimple(property);
  const formattedPrice = askingPrice 
    ? new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: priceCurrency,
        maximumFractionDigits: 0 
      }).format(askingPrice)
    : 'Contact for price';

  return (
    <div className={`bg-white rounded-lg overflow-hidden border ${
      isSelected ? 'border-blue-500' : 'border-gray-200'
    }`}>
      {/* Image */}
      <div className="relative w-full h-48 bg-gray-200">
        {property.primaryImageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={property.primaryImageUrl}
            alt={property.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No Image
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
          {property.name}
        </h3>
        <p className="text-xl font-bold text-blue-600 mb-2">
          {formattedPrice}
        </p>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          {property.bedrooms && (
            <span>{property.bedrooms} beds</span>
          )}
          {property.bathrooms && (
            <span>{property.bathrooms} baths</span>
          )}
          {property.totalFloorArea && (
            <span>{property.totalFloorArea} {property.totalFloorAreaUnit || 'sqft'}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Default Map Renderer (Placeholder)
 * 
 * Shows a placeholder until themes provide their own map implementation.
 */
function DefaultMapRenderer({
  properties,
  selectedPropertyId,
  hoveredPropertyId,
  onMarkerClick,
}: MapRendererProps) {
  return (
    <div className="w-full h-full bg-gray-100 flex items-center justify-center relative">
      {/* Placeholder Map */}
      <div className="text-center p-8">
        <div className="text-6xl mb-4">🗺️</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          Map View
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {properties.length} {properties.length === 1 ? 'property' : 'properties'} available
        </p>
        <p className="text-gray-500 text-xs">
          Integrate your preferred map library
        </p>
      </div>

      {/* Mock Property Markers */}
      <div className="absolute inset-0 pointer-events-auto">
        {properties.map((property, index) => {
          // Random positions for demonstration
          const left = 20 + (index * 15) % 70;
          const top = 20 + (index * 20) % 60;
          
          return (
            <button
              key={property.id}
              className={`absolute w-10 h-10 rounded-full border-2 transition-all ${
                selectedPropertyId === property.id || hoveredPropertyId === property.id
                  ? 'bg-red-500 border-white scale-125 shadow-lg z-10'
                  : 'bg-blue-500 border-white hover:scale-110'
              }`}
              style={{ left: `${left}%`, top: `${top}%` }}
              onClick={(e) => {
                e.stopPropagation();
                onMarkerClick(property);
              }}
            >
              <span className="flex items-center justify-center h-full text-white text-xs font-bold">
                ${Math.round((extractBestPriceSimple(property).amount || 0) / 1000)}k
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

